import { supabase } from './supabase';

export interface AnalyticsConfig {
  id: string;
  business_id: string;
  provider: 'custom' | 'ga4' | 'mixpanel';
  is_active: boolean;
  config: Record<string, any>;
  tracking_code?: string;
  created_at: string;
  updated_at: string;
}

export interface AnalyticsEvent {
  id: string;
  business_id: string;
  event_type: string;
  event_data: Record<string, any>;
  page_url?: string;
  referrer?: string;
  user_agent?: string;
  ip_address?: string;
  session_id?: string;
  visitor_id?: string;
  timestamp: string;
}

export interface AnalyticsMetrics {
  page_views: number;
  unique_visitors: number;
  sessions: number;
  form_submissions: number;
  clicks: number;
  top_pages: Array<{ page: string; views: number }>;
  traffic_sources: Array<{ source: string; visits: number }>;
}

export interface AnalyticsData {
  date: string;
  metrics: AnalyticsMetrics;
}

// Analytics Configuration Service
export const analyticsService = {
  // Get analytics configuration for a business
  async getConfig(businessId: string): Promise<AnalyticsConfig | null> {
    const { data, error } = await supabase
      .from('business_analytics_config')
      .select('*')
      .eq('business_id', businessId)
      .single();

    if (error) {
      console.error('Error fetching analytics config:', error);
      return null;
    }

    return data;
  },

  // Create or update analytics configuration
  async saveConfig(config: Partial<AnalyticsConfig>): Promise<AnalyticsConfig | null> {
    const { data, error } = await supabase
      .from('business_analytics_config')
      .upsert(config, { onConflict: 'business_id' })
      .select()
      .single();

    if (error) {
      console.error('Error saving analytics config:', error);
      return null;
    }

    return data;
  },

  // One-line loader. The script itself is served by the analytics-script function,
  // so collection fixes reach every site that has this tag.
  generateTrackingCode(businessId: string): string {
    const apiUrl = import.meta.env.VITE_SUPABASE_URL;
    return `<script src="${apiUrl}/functions/v1/analytics-script?business=${businessId}"></script>`;
  },

  // Get analytics data for a date range
  async getAnalyticsData(
    businessId: string, 
    startDate: string, 
    endDate: string
  ): Promise<AnalyticsData[]> {
    const { data, error } = await supabase
      .from('analytics_data_cache')
      .select('*')
      .eq('business_id', businessId)
      .gte('date', startDate)
      .lte('date', endDate)
      .order('date', { ascending: true });

    if (error) {
      console.error('Error fetching analytics data:', error);
      return [];
    }

    return data || [];
  },

  // Get real-time analytics for today
  async getRealTimeAnalytics(businessId: string): Promise<AnalyticsMetrics | null> {
    const today = new Date().toISOString().split('T')[0];
    
    const { data, error } = await supabase
      .rpc('aggregate_analytics_data', {
        p_business_id: businessId,
        p_date: today
      });

    if (error) {
      console.error('Error fetching real-time analytics:', error);
      return null;
    }

    return data;
  },

  // Get analytics events for debugging
  async getEvents(
    businessId: string, 
    limit: number = 100
  ): Promise<AnalyticsEvent[]> {
    const { data, error } = await supabase
      .from('analytics_events')
      .select('*')
      .eq('business_id', businessId)
      .order('timestamp', { ascending: false })
      .limit(limit);

    if (error) {
      console.error('Error fetching analytics events:', error);
      return [];
    }

    return data || [];
  }
};
